import { useId, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';

import { LANGUAGE_LABELS, SUPPORTED_LANGUAGES } from '@core/i18n';

import { useChangeLanguage } from '../hooks/useChangeLanguage';

/**
 * Language picker for the account menu and the sign-in screens.
 *
 * Each language is named in itself, so a user lost in a language they cannot
 * read still recognises their own.
 */
export function LanguageSwitcher({ className = '' }) {
  const { t } = useTranslation();
  const { changeLanguage, language, isSaving } = useChangeLanguage();
  const [failed, setFailed] = useState(false);
  const selectId = useId();

  const handleChange = async (event) => {
    setFailed(false);
    try {
      await changeLanguage(event.target.value);
    } catch {
      setFailed(true);
    }
  };

  return (
    <div className={className}>
      <label htmlFor={selectId} className="flex items-center gap-2 text-xs font-medium text-[var(--color-text)]">
        <Icon icon="mdi:translate" className="size-4 shrink-0 text-[var(--color-text-muted)]" />
        <span className="sr-only">{t('languageSwitcher.label')}</span>
        <select
          id={selectId}
          value={language}
          onChange={handleChange}
          disabled={isSaving}
          className="cursor-pointer w-full rounded-lg border border-[var(--color-border)] bg-white py-1.5 pe-8 ps-2.5 text-xs text-[var(--color-text)] focus:border-[var(--color-accent)] focus:ring-1 focus:ring-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {SUPPORTED_LANGUAGES.map((code) => (
            <option key={code} value={code} lang={code}>{LANGUAGE_LABELS[code]}</option>
          ))}
        </select>
      </label>
      {failed && <p role="alert" className="mt-1 text-[10px] text-[var(--color-danger)]">{t('languageSwitcher.saveFailed')}</p>}
    </div>
  );
}

LanguageSwitcher.propTypes = {
  className: PropTypes.string,
};
